/**
 * Phase 550 wave 2 task 2.1 (card #1999) — the decision-dialog's field logic, with NO browser
 * underneath it.
 *
 * WHY THIS MODULE IS SEPARATE FROM THE SCREEN. Seeding, visibility, blocking and
 * submission-building are pure data work, but they were about to be born inside
 * `JobDetailScreen.tsx` — a module that imports React, the design system and the query client.
 * Anything living there is unreachable from a test in this app: `vitest.config.ts` runs
 * `environment: "node"` and there is no DOM harness, so the rules that decide whether an approval
 * can even be SUBMITTED would have had no unit test at all, only the text-level greps wave 3
 * adds. They live here instead, in a module that is DOM-FREE BY CONSTRUCTION exactly as
 * `serverRefusal.ts` is: ZERO imports, and no reference to `fetch`, `Response`, `RequestInit`,
 * `Headers`, `window`, `document`, `localStorage` or any MSAL type — so the node-only typecheck
 * project (`tsconfig.integration.json`, `types: ["node"]`, `lib` inherited `["ES2022"]`) stays
 * green when the test tree reaches into `src`.
 *
 * WHAT THIS MODULE IS NOT ALLOWED TO KNOW (AC-3, and the reason it is this small). Every RULE
 * about these fields was resolved server-side by the one reader the staff approval form and the
 * submit-time validator already go through (`ApprovalGateDecisionProjection`): which fields
 * exist, their order, their labels, their options, whether each is required, what the file
 * computes for it, and WHICH SIBLING reveals a reason field. This module re-derives none of it.
 * It never inspects `computed_from`, never pairs a reason field to an anchor by name, and holds
 * no band, option, label or role literal. It only READS `required_when`, which arrived resolved.
 * Grep this file for `computed_from`, `override` or `band` and you find nothing — that absence is
 * the invariant, not an accident.
 *
 * WHAT "UNANSWERED" MEANS, and why it is the engine's definition and not ours. The validator
 * stringifies and TRIMS each submitted value and treats a zero-length result as not submitted at
 * all — "Absent, JSON null, and whitespace are ALL unanswered. A cleared control is an unanswered
 * question, never a silent agreement with whatever was pre-filled."
 * (`ApprovalGatePayloadValidator.cs:729-742`). `isBlocked` and `buildSubmission` below use that
 * same test, which is what makes two things true at once: a hidden reason field is OMITTED rather
 * than sent blank, and confirming a computed value as-is SENDS that value explicitly. The second
 * is why the decisions door keeps `acceptComputed: false` — the console does not ask the engine
 * to stamp a value on its behalf; the approver's submission says what they approved.
 */

/**
 * The reveal predicate as it arrives on the wire, structurally. Mirrors `RequiredWhen` in
 * `types.ts` — restated rather than imported, because an import is precisely what this module may
 * not have. The two are pinned to each other by `test/decision-fields.test.ts`, which assigns a
 * real `ApprovalGateFieldView` into `DecisionField` and lets the typecheck fail if they drift.
 */
export interface DecisionFieldReveal {
  /** The declared NAME of the sibling field being compared. */
  field: string;
  /** The comparison — always `ne` today. See `isVisible` for why nothing branches on it. */
  op: string;
  /** The computed value a change is measured against. */
  value: string;
}

/**
 * The members of one declared field that the LOGIC needs — a structural subset of
 * `ApprovalGateFieldView`. `label`, `type`, `options`, `source_label` and `unresolved_reason` are
 * deliberately absent: they are render-only, and a rule that could see them could start switching
 * on one.
 */
export interface DecisionField {
  /** The declared payload key an answer lands under. */
  name: string;
  /** The ELEVATED requiredness the engine resolved — never a declared flag re-read here. */
  required: boolean;
  /** What the file answers this field with, if anything. */
  computed_value?: string;
  /** The reveal predicate, or absent for a field shown unconditionally. */
  required_when?: DecisionFieldReveal;
}

/** The dialog's answer state: one string per declared field name, `""` for unanswered. */
export type DecisionAnswers = Record<string, string>;

/**
 * The engine's own test for "answered", as a type guard so the submission builder below narrows
 * instead of casting: present, and not merely whitespace.
 */
function isAnswered(answer: string | undefined): answer is string {
  return answer !== undefined && answer.trim().length > 0;
}

/**
 * The dialog's opening state: every declared field pre-filled with what the FILE computed for it,
 * or `""` when the file computed nothing.
 *
 * Every field gets a key, including the hidden ones, so `isVisible` below is always comparing
 * against a real answer rather than against `undefined`, and so re-opening the dialog cannot
 * leave a stale key behind.
 */
export function seedAnswers(fields: readonly DecisionField[]): DecisionAnswers {
  const answers: DecisionAnswers = {};
  for (const field of fields) {
    answers[field.name] = field.computed_value ?? "";
  }
  return answers;
}

/**
 * Is this field shown? Yes, unless it declares a reveal predicate whose anchor still holds its
 * computed value — i.e. a reason field stays hidden while the approver agrees with the file, and
 * appears the moment they differ.
 *
 * NOTHING BRANCHES ON `op`. It is a one-member vocabulary the engine carries explicitly so the
 * predicate is self-describing; a client `switch` over it would be a second owner of a comparison
 * the engine already resolved, and would silently mis-render the day a second member is minted
 * rather than failing loudly server-side.
 */
export function isVisible(
  field: DecisionField,
  answers: DecisionAnswers,
): boolean {
  const reveal = field.required_when;
  if (reveal === undefined) return true;
  return answers[reveal.field] !== reveal.value;
}

/**
 * May the operator submit yet? No, while some VISIBLE required field is unanswered.
 *
 * This is what turns the engine's `reason_required` / `decision_fields_required` 400 into a
 * disabled button: the browser asks for what the gate declares instead of letting the operator
 * meet the obligation at a refusal. A HIDDEN required field blocks nothing — its predicate says
 * it is not owed.
 *
 * A gate declaring no fields yields `false` by construction, so a dialog for such a gate is
 * exactly as submittable as it was before this phase.
 */
export function isBlocked(
  fields: readonly DecisionField[],
  answers: DecisionAnswers,
): boolean {
  return fields.some(
    (field) =>
      field.required &&
      isVisible(field, answers) &&
      !isAnswered(answers[field.name]),
  );
}

/**
 * The `decision_fields` object to post: every VISIBLE field carrying a non-blank answer, and
 * nothing else.
 *
 * Two omissions are load-bearing, both of them the engine's own definition of unanswered
 * (`ApprovalGatePayloadValidator.cs:729-742`):
 *   - a HIDDEN field is omitted entirely — sending a reason blank alongside a confirmed band
 *     would be the console asserting an empty answer to a question that was never asked;
 *   - a visible-but-blank OPTIONAL field is omitted too, rather than sent as `""`.
 * Values are passed through as typed: the validator stringifies and trims before it checks
 * options, so normalising here would be a second owner of that one step.
 */
export function buildSubmission(
  fields: readonly DecisionField[],
  answers: DecisionAnswers,
): Record<string, string> {
  const submission: Record<string, string> = {};
  for (const field of fields) {
    if (!isVisible(field, answers)) continue;
    const answer = answers[field.name];
    if (!isAnswered(answer)) continue;
    submission[field.name] = answer;
  }
  return submission;
}
